import type { Plan, PlanBillingPeriod } from '@/core/domain';
import {
  PLAN_BILLING_PERIOD_LABELS,
  PLAN_BILLING_PERIOD_MONTHS,
  PLAN_BILLING_PERIODS,
  formatPlanPriceBrlFromCents,
  isFreePlan,
} from '@/core/domain';
import {
  computeEquivalentMonthlyCents,
  computeSavingsVsMonthlyStackedCents,
} from '@/features/plans/presentation/constants/plan-offers-display';
import {
  CHOOSE_PLAN_INTERNAL_FREE_ACTIVATING_LABEL,
  CHOOSE_PLAN_INTERNAL_FREE_BILLING_NOTE,
  CHOOSE_PLAN_INTERNAL_FREE_HIGHLIGHT_LABEL,
  CHOOSE_PLAN_SUBSCRIBE_INTERNAL_FREE_CTA,
} from '@/features/plans/presentation/composables/constants/choose-plan-screen.constants';
import type { ChoosePlanPremiumPeriodRef } from '@/features/plans/presentation/utils/choose-plan-dual-layout';

/** Ordem visual das abas no card premium (seleção padrão: semestral no bridge). */
export const CHOOSE_PLAN_SCREEN_BILLING_ORDER: readonly PlanBillingPeriod[] = [
  'monthly',
  'semiannual',
  'annual',
];

const PRICE_SUB: Record<PlanBillingPeriod, string> = {
  monthly: '/mês',
  semiannual: '/semestre',
  annual: '/ano',
};

const BILLING_NOTE: Record<PlanBillingPeriod, string> = {
  monthly: 'Cobrança todo mês',
  semiannual: 'Cobrança a cada 6 meses',
  annual: 'Cobrança uma vez por ano',
};

export interface ChoosePlanRowDisplay {
  planId: string;
  title: string;
  priceLine: string;
  priceSub: string;
  billingNote: string;
  highlight?: boolean;
  highlightLabel?: string;
  equivalentMonthlyCents?: number;
  savingsTotalCents?: number;
  subscribeCtaLabel: string;
  checkoutLoadingLabel: string;
}

function sortPlansByOrder(a: Plan, b: Plan): number {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.title.localeCompare(b.title, 'pt-BR');
}

/**
 * Linhas para listagens legadas (ex.: seção Planos no app) a partir do plano pago + variantes.
 */
export function buildChoosePlanRowsFromPlans(plans: Plan[]): ChoosePlanRowDisplay[] {
  const active = plans.filter((p) => p.is_active);
  const paid = active.filter((p) => p.kind === 'paid').sort(sortPlansByOrder)[0];
  if (!paid?.billing_variants) return [];

  const monthlyBaselineCents = paid.billing_variants.monthly?.price_brl_cents ?? null;
  const rows: ChoosePlanRowDisplay[] = [];

  const freePlans = active.filter(isFreePlan).sort(sortPlansByOrder);
  for (const plan of freePlans) {
    rows.push({
      planId: plan.id,
      title: plan.title,
      priceLine: formatPlanPriceBrlFromCents(0),
      priceSub: '',
      billingNote: CHOOSE_PLAN_INTERNAL_FREE_BILLING_NOTE,
      highlightLabel: CHOOSE_PLAN_INTERNAL_FREE_HIGHLIGHT_LABEL,
      subscribeCtaLabel: CHOOSE_PLAN_SUBSCRIBE_INTERNAL_FREE_CTA,
      checkoutLoadingLabel: CHOOSE_PLAN_INTERNAL_FREE_ACTIVATING_LABEL,
    });
  }

  for (const period of CHOOSE_PLAN_SCREEN_BILLING_ORDER) {
    const variant = paid.billing_variants[period];
    if (!variant) continue;

    const months = PLAN_BILLING_PERIOD_MONTHS[period];
    const totalCents = variant.price_brl_cents;

    let equivalentMonthlyCents: number | undefined;
    if (period !== 'monthly' && months > 0) {
      equivalentMonthlyCents = computeEquivalentMonthlyCents(totalCents, months);
    }

    let savingsTotalCents: number | undefined;
    if (period !== 'monthly' && monthlyBaselineCents != null && months > 0) {
      savingsTotalCents = computeSavingsVsMonthlyStackedCents(
        totalCents,
        months,
        monthlyBaselineCents
      );
    }

    rows.push({
      planId: paid.id,
      title: `${paid.title} — ${PLAN_BILLING_PERIOD_LABELS[period]}`,
      priceLine: formatPlanPriceBrlFromCents(totalCents),
      priceSub: PRICE_SUB[period],
      billingNote: BILLING_NOTE[period],
      highlight: period === 'semiannual',
      highlightLabel: period === 'semiannual' ? 'Mais adquirido' : undefined,
      equivalentMonthlyCents,
      savingsTotalCents,
      subscribeCtaLabel: 'Assinar',
      checkoutLoadingLabel: 'Abrindo checkout',
    });
  }

  return rows;
}

export type { ChoosePlanPremiumPeriodRef };
